import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './en.json';
import bn from './bn.json';

/**
 * i18n — spec 7.6: react-i18next, English + বাংলা, toggle in header.
 *
 * Resources are BUNDLED (imported, not fetched) so init is synchronous. That
 * matters twice: the prerenderer (entry-server.tsx) runs renderToString with no
 * async boundary for a language load, and the browser must render the first
 * paint in the chosen language without a flash of keys.
 *
 * The chosen language persists in localStorage under `maktech_lang`. The
 * prerenderer always renders English — crawlers get a stable English page, and
 * the client re-renders in the saved language on hydrate.
 *
 * bn.json carries the Bangla strings the prototype already had; everything else
 * is marked `TODO_BN:` so a translator can find it, and falls back to English
 * at runtime via fallbackLng so nothing renders a raw key.
 */

export const LANGS = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'bn', label: 'বাংলা', short: 'বাং' },
] as const;

export type LangCode = (typeof LANGS)[number]['code'];

const STORAGE_KEY = 'maktech_lang';

function savedLang(): LangCode {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'en' || v === 'bn') return v;
  } catch {
    /* SSR / private mode */
  }
  return 'en';
}

void i18n.use(initReactI18next).init({
  resources: { en: { translation: en }, bn: { translation: bn } },
  lng: savedLang(),
  fallbackLng: 'en',
  interpolation: { escapeValue: false }, // React already escapes
  returnEmptyString: false, // an empty bn value falls back to en
});

export function setLang(code: LangCode): void {
  try {
    localStorage.setItem(STORAGE_KEY, code);
  } catch {
    /* ignore */
  }
  void i18n.changeLanguage(code);
  if (typeof document !== 'undefined') document.documentElement.lang = code;
}

export default i18n;
